import { LATEST_DATA_PROCESSING_AGREEMENT, LATEST_TERMS_OF_SERVICE } from '@Constants/legalDocumentVersions';
import { Permission } from '@Constants/Permission.g';
import { UserText } from '@Constants/UserText';
import { useHasPermission } from '@Contexts/appContext';
import { useAcceptClientLegalDocuments } from '@Hooks/Permissions';
import { useLogout } from '@Hooks/useMsal';
import { Button, FlexRow, Modal } from '@synqo/frontend-component-library';
import { FC } from 'react';
import { Link } from 'react-router';

interface AcceptLegaleseProps {
    refetchAuthorization: () => void;
    clientId: string;
}

export const AcceptClientLegalese: FC<AcceptLegaleseProps> = ({ clientId, refetchAuthorization }) => {
    const hasPermission = useHasPermission();

    const canAccept = hasPermission(Permission.PermissionsClientAcceptLegalDocuments);

    return (
        <Modal
            isOpen={true}
            withoutCloseButton={true}
            onClose={() => {}}
            title={'Auftragsdatenverarbeitung und allgemeine Nutzungsvereinbarung'}
        >
            <ClientLegaleseHeader />
            {canAccept ? (
                <ClientLegaleseFooter clientId={clientId} refetchAuthorization={refetchAuthorization} />
            ) : (
                <ClientLegaleseRequiresAdmin />
            )}
        </Modal>
    );
};

function ClientLegaleseHeader() {
    return (
        <p>
            Um {UserText.APP_NAME} nutzen zu können, muss der{' '}
            <Link
                className="text-(--mantine-color-text-5) hover:text-(--mantine-color-text-7) underline"
                to={'/auftragsdatenverarbeitung.pdf'}
                target="_blank"
            >
                Auftragsdatenverarbeitung
            </Link>{' '}
            und{' '}
            <Link
                className="text-(--mantine-color-text-5) hover:text-(--mantine-color-text-7) underline"
                to={'/allgemeine-nutzungsvereinbarung.pdf'}
                target="_blank"
            >
                allgemeinen Nutzungsvereinbarung
            </Link>{' '}
            zugestimmt werden.
        </p>
    );
}

function ClientLegaleseFooter({ clientId, refetchAuthorization }: AcceptLegaleseProps) {
    const logout = useLogout();
    const { mutateAsync: acceptLegalDocuments } = useAcceptClientLegalDocuments();

    return (
        <FlexRow justify={'space-between'} className={'pt-5'}>
            <Button variant={'warning'} onClick={logout} label={'Nicht zustimmen (ausloggen)'} appearance="filled">
                Nicht zustimmen (ausloggen)
            </Button>
            <Button
                variant={'success'}
                appearance={'filled'}
                onClick={async () => {
                    await acceptLegalDocuments({
                        clientId,
                        dataProcessingAgreementVersion: LATEST_DATA_PROCESSING_AGREEMENT,
                        termsOfServiceVersion: LATEST_TERMS_OF_SERVICE,
                    });
                    refetchAuthorization();
                }}
                label={'Zustimmen'}
            >
                Zustimmen
            </Button>
        </FlexRow>
    );
}

function ClientLegaleseRequiresAdmin() {
    const logout = useLogout();
    return (
        <FlexRow justify={'space-between'} className={'pt-5'}>
            <p>
                Sie haben keine Berechtigung, diese Zustimmung zu geben. Bitte wenden Sie sich an Ihren Administrator.
            </p>
            <Button variant={'warning'} onClick={logout} label={'Nicht zustimmen (ausloggen)'} appearance="filled">
                Abmelden
            </Button>
        </FlexRow>
    );
}
